import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import { TicketQueue, DEFAULT_TICKET_TYPES } from '../components/tickets';
import TicketActionModal from '../components/tickets/TicketActionModals';
import request from '../utils/request';


// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ticket Queue Screen
 * 
 * Displays the dispatcher's personal ticket queue with:
 * - Working tickets (in progress)
 * - Queued tickets sorted by urgency/priority
 * - Order story side panel
 */
export default function TicketQueueScreen() {
  const { adminInfo, token } = useSelector(s => s.login);

  const currentUserName = adminInfo ? `${adminInfo.firstName} ${adminInfo.lastName}` : 'Demo User';
  // Backend returns 'id' not '_id'
  const currentUserId = adminInfo?.id || adminInfo?._id || 'demo_user';
  // isAdmin can come from JWT payload or computed from role
  const isAdmin = adminInfo?.isAdmin === true || adminInfo?.role === 'admin';

  const [orders, setOrders] = useState([]);
  const [fetchedTickets, setFetchedTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state for ticket action
  const [activeTicket, setActiveTicket] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // Socket state for real-time updates
  const [socket, setSocket] = useState(null);


  // Function to fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      const res = await request.get('/tickets?includeOrder=true'); // GET /tickets returns { success: true, tickets: [...] }
      if (res.data.success) {
        // Map MongoDB _id to id and convert ObjectIds to strings for frontend compatibility
        // Preserve the full populated order object
        const mappedTickets = (res.data.tickets || []).map(t => ({
          ...t,
          id: t._id || t.id,
          type: t.ticketTypeId || t.type,
          // Keep the full order object if populated, otherwise just the ID
          orderData: t.orderId?._id ? t.orderId : null,
          orderId: t.orderId?._id || t.orderId?.toString?.() || t.orderId,
          assignedTo: t.assignedTo?._id || t.assignedTo?.toString?.() || t.assignedTo,
        }));
        console.log('[TicketQueue] API Response:', {
          ticketCount: mappedTickets.length,
          userId: res.data.userId,
          isAdmin: res.data.isAdmin
        });
        setFetchedTickets(mappedTickets);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch real data on mount
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Socket connection for real-time ticket updates (token from Redux, not localStorage)
  useEffect(() => {
    if (!token) {
      return;
    }

    const socketOrigin = process.env.REACT_APP_SOCKET_ORIGIN || 'http://localhost:5001';
    const ticketSocket = io(socketOrigin, {
      path: '/rt',
      transports: ['websocket'],
      withCredentials: true,
      auth: { token }
    });

    ticketSocket.on('connect', () => {
      console.log('[TicketQueue] 🔌 Socket connected successfully');
      ticketSocket.emit('tickets:join');
      console.log('[TicketQueue] 📡 Joined tickets room');

      // Join user-specific room for personal ticket updates
      if (currentUserId) {
        ticketSocket.emit('user:join', { userId: currentUserId });
        console.log('[TicketQueue] 👤 Joined user room:', currentUserId);
      }
    });

    ticketSocket.on('connect_error', (err) => {
      console.error('[TicketQueue] Socket connect error:', err);
    });

    ticketSocket.on('disconnect', (reason) => {
      console.warn('[TicketQueue] 🔌 Socket disconnected:', reason);
    });

    // Debug: Listen for all events
    ticketSocket.onAny((event, ...args) => {
      console.log('[TicketQueue] 📡 WebSocket event received:', event, args);
    });

    // Listen for ticket events with real-time state updates
    ticketSocket.on('ticket:created', (data) => {
      console.log('[TicketQueue] 🎫 Ticket created event received:', data);
      console.log('[TicketQueue] Ticket data:', JSON.stringify(data.ticket, null, 2));
      const newTicket = {
        ...data.ticket,
        id: data.ticket._id || data.ticket.id,
        type: data.ticket.ticketTypeId || data.ticket.type,
        orderData: data.ticket.orderId?._id ? data.ticket.orderId : null,
        orderId: data.ticket.orderId?._id || data.ticket.orderId?.toString?.() || data.ticket.orderId,
        assignedTo: data.ticket.assignedTo?._id || data.ticket.assignedTo?.toString?.() || data.ticket.assignedTo,
      };
      console.log('[TicketQueue] Adding new ticket to state:', newTicket.id);
      setFetchedTickets(prev => [newTicket, ...prev]);
    });

    ticketSocket.on('ticket:updated', (data) => {
      console.log('[TicketQueue] Ticket updated:', data.ticket._id, data.changes);
      setFetchedTickets(prev => prev.map(ticket =>
        (ticket.id === data.ticket._id || ticket._id === data.ticket._id)
          ? { ...ticket, ...data.ticket }
          : ticket
      ));
    });

    ticketSocket.on('ticket:assigned', (data) => {
      console.log('[TicketQueue] Ticket assigned:', data.ticket._id, 'to:', data.assignedToName);
      setFetchedTickets(prev => prev.map(ticket =>
        (ticket.id === data.ticket._id || ticket._id === data.ticket._id)
          ? {
              ...ticket,
              ...data.ticket,
              assignedTo: data.assignedTo,
              assignedToName: data.assignedToName
            }
          : ticket
      ));
    });

    ticketSocket.on('ticket:status_changed', (data) => {
      console.log('[TicketQueue] Ticket status changed:', data.ticket._id, data.oldStatus, '->', data.newStatus);
      setFetchedTickets(prev => prev.map(ticket =>
        (ticket.id === data.ticket._id || ticket._id === data.ticket._id)
          ? { ...ticket, ...data.ticket, status: data.newStatus }
          : ticket
      ));
    });

    ticketSocket.on('ticket:completed', (data) => {
      console.log('[TicketQueue] Ticket completed:', data.ticket._id);
      // Remove completed tickets from the list (they shouldn't show in active queue)
      setFetchedTickets(prev => prev.filter(ticket =>
        !(ticket.id === data.ticket._id || ticket._id === data.ticket._id)
      ));
    });

    ticketSocket.on('ticket:escalated', (data) => {
      console.log('[TicketQueue] Ticket escalated:', data.ticket._id, 'to priority:', data.newPriority);
      setFetchedTickets(prev => prev.map(ticket =>
        (ticket.id === data.ticket._id || ticket._id === data.ticket._id)
          ? { ...ticket, ...data.ticket, priority: data.newPriority }
          : ticket
      ));
    });

    // Listen for real-time metrics updates
    ticketSocket.on('tickets:metrics', (data) => {
      console.log('[TicketQueue] Metrics update:', data.metrics);
      // Could update local state with metrics if needed
    });

    // Listen for personal metrics updates
    ticketSocket.on('tickets:my_metrics', (data) => {
      console.log('[TicketQueue] Personal metrics update:', data.metrics);
      // Could update personal dashboard metrics
    });

    setSocket(ticketSocket);

    return () => {
      console.log('[TicketQueue] Cleaning up socket');
      ticketSocket.disconnect();
    };
  }, [token, currentUserId]);

  // Use only real fetched tickets
  const allTickets = fetchedTickets || [];


  // Demo: Add new tickets with animations
  const handleClaimTicket = useCallback(async (ticket) => {
    try {
      const res = await request.patch(`/tickets/${ticket.id || ticket._id}/claim`);
      if (res.data.success) {
        // Update local state
        setFetchedTickets(prev => prev.map(t => 
          (t.id === ticket.id || t._id === ticket._id) 
            ? { ...t, assignedTo: adminInfo?.id || adminInfo?._id, assignedToName: `${adminInfo?.firstName} ${adminInfo?.lastName}`, status: 'assigned' }
            : t
        ));
      }
    } catch (err) {
      console.error('Error claiming ticket:', err);
    }
  }, [adminInfo]);

  // Fetch order data for modal
  const fetchOrderForTicket = useCallback(async (orderId) => {
    if (!orderId) {
      console.log('[TicketQueue] No orderId provided');
      return null;
    }
    try {
      setLoadingOrder(true);
      console.log('[TicketQueue] Fetching order:', orderId);
      
      // Try to get from tickets endpoint which has order populated
      const ticketRes = await request.get(`/tickets?includeOrder=true`);
      if (ticketRes.data?.tickets && Array.isArray(ticketRes.data.tickets)) {
        const ticketWithOrder = ticketRes.data.tickets.find(t =>
          (t.orderId?._id || t.orderId) === orderId
        );
        if (ticketWithOrder?.orderId?._id) {
          console.log('[TicketQueue] Found order from ticket:', ticketWithOrder.orderId);
          return ticketWithOrder.orderId;
        }
      }
      
      // Fallback: try direct order fetch
      const res = await request.get(`/orders/${orderId}`);
      console.log('[TicketQueue] Order response:', res.data);
      // Handle different response formats - API returns order directly
      if (res.data?._id) return res.data;
      if (res.data?.order) return res.data.order;
      return res.data;
    } catch (err) {
      console.error('Error fetching order:', err?.response?.data || err.message);
      // Return a minimal order object so modal can still display
      return { _id: orderId, uniqueId: 'Unknown' };
    } finally {
      setLoadingOrder(false);
    }
  }, []);

  // Open modal for any ticket (can be called on click or start)
  const handleOpenTicketModal = useCallback(async (ticket) => {
    console.log('[TicketQueue] Opening modal for ticket:', ticket);
    setActiveTicket(ticket);
    
    // Use cached order data if available, otherwise fetch
    if (ticket.orderData) {
      console.log('[TicketQueue] Using cached order data:', ticket.orderData);
      setActiveOrder(ticket.orderData);
    } else if (ticket.orderId) {
      const orderData = await fetchOrderForTicket(ticket.orderId);
      console.log('[TicketQueue] Fetched order data:', orderData);
      setActiveOrder(orderData);
    } else {
      setActiveOrder(null);
    }
  }, [fetchOrderForTicket]);

  const handleStartTicket = useCallback(async (ticket) => {
    try {
      const res = await request.patch(`/tickets/${ticket.id || ticket._id}/start`);
      if (res.data.success) {
        const updatedTicket = { ...ticket, status: 'in_progress', startedAt: new Date().toISOString() };
        setFetchedTickets(prev => prev.map(t => 
          (t.id === ticket.id || t._id === ticket._id) 
            ? updatedTicket
            : t
        ));
        
        // Open the modal with the updated ticket
        await handleOpenTicketModal(updatedTicket);
      }
    } catch (err) {
      console.error('Error starting ticket:', err);
    }
  }, [handleOpenTicketModal]);

  const handleCompleteTicket = useCallback(async (ticket, completionData = {}) => {
    try {
      const res = await request.patch(`/tickets/${ticket.id || ticket._id}/complete`, completionData);
      if (res.data.success) {
        setFetchedTickets(prev => prev.map(t => 
          (t.id === ticket.id || t._id === ticket._id) 
            ? { ...t, status: 'completed', completedAt: new Date().toISOString() }
            : t
        ));
      }
    } catch (err) {
      console.error('Error completing ticket:', err);
    }
  }, []);

  const handlePauseTicket = useCallback(async (ticket) => {
    try {
      const res = await request.patch(`/tickets/${ticket.id || ticket._id}/pause`);
      if (res.data.success) {
        setFetchedTickets(prev => prev.map(t => 
          (t.id === ticket.id || t._id === ticket._id) 
            ? { ...t, status: 'pending' }
            : t
        ));
      }
    } catch (err) {
      console.error('Error pausing ticket:', err);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.get('/tickets?includeOrder=true');
      if (res.data.success) {
        const mappedTickets = res.data.tickets.map(t => ({
          ...t,
          id: t._id || t.id,
          type: t.ticketTypeId || t.type,
          orderData: t.orderId?._id ? t.orderId : null,
          orderId: t.orderId?._id || t.orderId?.toString?.() || t.orderId,
          assignedTo: t.assignedTo?._id || t.assignedTo?.toString?.() || t.assignedTo,
        }));
        setFetchedTickets(mappedTickets);
      }
    } catch (err) {
      console.error('Error refreshing tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading ticket queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-screen overflow-hidden relative">
      <TicketQueue
        tickets={allTickets}
        orders={orders}
        onClaimTicket={handleClaimTicket}
        onStartTicket={handleStartTicket}
        onCompleteTicket={handleCompleteTicket}
        onPauseTicket={handlePauseTicket}
        onRefresh={handleRefresh}
        onTicketClick={handleOpenTicketModal}
      />

      {/* Ticket Action Modal */}
      {activeTicket && (
        <TicketActionModal
          ticket={activeTicket}
          order={activeOrder}
          onClose={() => {
            setActiveTicket(null);
            setActiveOrder(null);
          }}
          onComplete={async () => {
            // Complete the ticket
            await handleCompleteTicket(activeTicket);
            setActiveTicket(null);
            setActiveOrder(null);
          }}
          onSaveData={async (data) => {
            // Save ticket-specific data to the order using dispatch endpoint
            try {
              if (activeOrder?._id) {
                await request.patch(`/orders/${activeOrder._id}/ticket-update`, data);
              }
              // Also save to ticket custom fields
              await request.patch(`/tickets/${activeTicket.id || activeTicket._id}/data`, { customFieldValues: data });
            } catch (err) {
              console.error('Error saving ticket data:', err);
            }
          }}
          onEscalate={async (ticket) => {
            // Escalate the ticket
            try {
              await request.patch(`/tickets/${ticket.id || ticket._id}/escalate`, {
                reason: 'Escalated from action modal'
              });
              handleRefresh();
            } catch (err) {
              console.error('Error escalating ticket:', err);
            }
          }}
        />
      )}
    </div>
  );
}
